import {createStyles, Group, Text, UnstyledButton} from '@mantine/core';
import React, {useState} from 'react';

import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';

import {MAX_BULK_PREDICTION_ITEMS} from '../ScientificModelConstants';
import type {BulkPredictionItem} from './BulkPredictionUtils';

const VISIBLE_CHIP_LIMIT = 10;

interface SelectedEntitiesAreaProps {
  items: Array<BulkPredictionItem>;
  onRemoveItem: (id: string) => void;
  onClearAll: () => void;
}

export default function SelectedEntitiesArea({
  items,
  onRemoveItem,
  onClearAll,
}: SelectedEntitiesAreaProps): JSX.Element {
  const {classes} = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);

  const visibleItems = isExpanded ? items : items.slice(0, VISIBLE_CHIP_LIMIT);
  const hiddenCount = items.length - VISIBLE_CHIP_LIMIT;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Text size="sm" weight={600}>
          Selected entities ({items.length}/{MAX_BULK_PREDICTION_ITEMS})
        </Text>
        {items.length > 0 && (
          <UnstyledButton onClick={onClearAll}>
            <Text size="xs" color="dimmed">
              Clear all
            </Text>
          </UnstyledButton>
        )}
      </div>
      <div className={classes.chipsArea}>
        {items.length === 0 ? (
          <div className={classes.placeholder}>None selected</div>
        ) : (
          <Group spacing="xs">
            {visibleItems.map((item) => (
              <BioentityChip
                key={item.id}
                id={item.id}
                size={ChipSize.SMALL}
                isLink={false}
                onRemove={() => {
                  onRemoveItem(item.id);
                }}
              />
            ))}
            {!isExpanded && hiddenCount > 0 && (
              <UnstyledButton
                className={classes.overflowButton}
                onClick={() => {
                  setIsExpanded(true);
                }}
              >
                +{hiddenCount} more
              </UnstyledButton>
            )}
          </Group>
        )}
      </div>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    marginBottom: theme.spacing.md,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.xs,
  },
  chipsArea: {
    minHeight: 44,
    maxHeight: 96,
    overflowY: 'auto',
    padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.colors.gray[2],
  },
  placeholder: {
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.sm,
    lineHeight: '24px',
    paddingLeft: theme.spacing.xs,
  },
  overflowButton: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.blue[6],
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
}));
